/**
 * PlayProjectClient - Unified client for Play Project platform
 *
 * Provides a single interface for:
 * - Reading data from the indexer API (collections, NFTs, users)
 * - Building blockchain transactions for TonConnect
 *
 * @example
 * ```typescript
 * import { PlayProjectClient } from '@playproject/sdk';
 *
 * const client = new PlayProjectClient({
 *   baseUrl: 'https://api.playproject.io'
 * });
 *
 * // Read data
 * const collections = await client.getCollections();
 * const nfts = await client.getCollectionNfts('EQ...');
 *
 * // Build transaction
 * const tx = client.createMintTransaction({
 *   collectionAddress: 'EQ...',
 *   quantity: 2,
 *   pricePerNft: toNano('15')
 * });
 *
 * // Send via TonConnect
 * await tonConnectUI.sendTransaction({
 *   validUntil: Date.now() + 5 * 60 * 1000,
 *   messages: [tx]
 * });
 * ```
 */
import { Address, beginCell } from '@ton/core';
import { PlayProjectError } from './errors.js';
import { storePublicMint, storeRegisterCollection, storeRequestReveal, storeUnverifyCollection, storeVerifyCollection, storeWithdraw, storeWithdrawFees, } from './messages.js';
import { CollectionSchema, CollectionStatsSchema, IndexedEventSchema, NftSchema, NftWithCollectionSchema, UserCollectionNftCountsSchema, UserStatsSchema, paginatedSchema, } from './schemas.js';
// =============================================================================
// Constants
// =============================================================================
// Gas constants — imported from canonical source (constants/gas.ts)
import { MINT_GAS_BASE, MINT_GAS_PER_NFT, REGISTRY_GAS, USER_REVEAL_GAS, WITHDRAW_GAS, } from '../constants/gas.js';
// Default TonAPI URLs
const DEFAULT_TONAPI_URLS = {
    testnet: 'https://testnet.tonapi.io',
    mainnet: 'https://tonapi.io',
};
// =============================================================================
// Client Implementation
// =============================================================================
const DEFAULT_TIMEOUT_MS = 15_000;
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_INITIAL_DELAY_MS = 300;
const MAX_RETRY_AFTER_MS = 30_000; // cap server-hinted Retry-After to 30s
export class PlayProjectClient {
    baseUrl;
    fetch;
    _network;
    _tonApiKey;
    _tonApiUrl;
    _apiKey;
    _timeoutMs;
    _maxRetries;
    _initialDelayMs;
    _validateResponses;
    _walletAddress;
    constructor(config) {
        // #631: Validate baseUrl up front so misconfiguration fails loudly here
        // rather than as an opaque `new URL()` throw deep inside a request.
        if (typeof config.baseUrl !== 'string' || config.baseUrl.trim() === '') {
            throw new Error('PlayProjectClient: baseUrl is required and must be a non-empty string');
        }
        if (!/^https?:\/\//i.test(config.baseUrl)) {
            throw new Error(`PlayProjectClient: baseUrl must start with http:// or https:// (got: ${config.baseUrl})`);
        }
        this.baseUrl = config.baseUrl.replace(/\/$/, '');
        this.fetch = config.fetch ?? globalThis.fetch.bind(globalThis);
        this._network = config.network ?? 'testnet';
        this._tonApiKey = config.tonApiKey;
        this._apiKey = config.apiKey;
        this._timeoutMs = config.timeout ?? DEFAULT_TIMEOUT_MS;
        this._maxRetries = config.retry?.maxRetries ?? DEFAULT_MAX_RETRIES;
        this._initialDelayMs = config.retry?.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
        this._validateResponses = config.validateResponses ?? false;
        // Determine TonAPI URL
        const customUrls = config.tonApiUrls ?? {};
        this._tonApiUrl =
            this._network === 'mainnet'
                ? (customUrls.mainnet ?? DEFAULT_TONAPI_URLS.mainnet)
                : (customUrls.testnet ?? DEFAULT_TONAPI_URLS.testnet);
    }
    /**
     * Set the connected wallet address for rate-limit bucketing (Spec 07).
     * Call after TonConnect wallet connection.
     */
    setWalletAddress(address) {
        this._walletAddress = address ?? undefined;
    }
    // ===========================================================================
    // Configuration Getters
    // ===========================================================================
    /** Get the configured network */
    get network() {
        return this._network;
    }
    /** Get the TonAPI URL for the configured network */
    get tonApiUrl() {
        return this._tonApiUrl;
    }
    /** Check if TonAPI key is configured */
    get hasTonApiKey() {
        return !!this._tonApiKey;
    }
    /** Get the full configuration (for debugging/display) */
    getConfig() {
        return {
            baseUrl: this.baseUrl,
            network: this._network,
            tonApiUrl: this._tonApiUrl,
            hasTonApiKey: this.hasTonApiKey,
        };
    }
    // ===========================================================================
    // Private Helpers
    // ===========================================================================
    /**
     * Fail-fast validation for address inputs. Rejects empty/whitespace and
     * anything Address.parse() can't decode (friendly + raw formats). Catches
     * obvious footguns (empty string, path traversal, malformed input) before
     * a pointless network round-trip to the indexer.
     */
    assertValidAddress(address, paramName = 'address') {
        if (typeof address !== 'string' || address.trim() === '') {
            throw new Error(`PlayProjectClient: ${paramName} is required and must be non-empty`);
        }
        try {
            Address.parse(address);
        }
        catch (err) {
            const reason = err instanceof Error ? err.message : String(err);
            throw new Error(`PlayProjectClient: ${paramName} is not a valid TON address (${reason})`);
        }
    }
    async requestOnce(path, params, schema) {
        const url = new URL(`${this.baseUrl}${path}`);
        if (params) {
            for (const [key, value] of Object.entries(params)) {
                if (value !== undefined && value !== null) {
                    url.searchParams.set(key, String(value));
                }
            }
        }
        const headers = {};
        if (this._apiKey) {
            headers['x-api-key'] = this._apiKey;
        }
        if (this._walletAddress) {
            headers['x-wallet-address'] = this._walletAddress;
        }
        const controller = new AbortController();
        let timedOut = false;
        const timeout = setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, this._timeoutMs);
        let response;
        try {
            response = await this.fetch(url.toString(), {
                headers: Object.keys(headers).length > 0 ? headers : undefined,
                signal: controller.signal,
            });
        }
        catch (err) {
            if (timedOut) {
                // Re-shape AbortError from our own timeout as a typed PlayProjectError
                // so callers can distinguish a stall from a user-initiated cancel.
                throw new PlayProjectError(`Request timed out after ${this._timeoutMs}ms`, 0, 'TIMEOUT', {
                    path,
                    timeoutMs: this._timeoutMs,
                });
            }
            throw err;
        }
        finally {
            clearTimeout(timeout);
        }
        if (!response.ok) {
            const body = await response.json().catch(() => ({ error: 'Unknown error' }));
            const message = (body && typeof body === 'object' && typeof body.error === 'string'
                ? body.error
                : null) ?? `HTTP ${response.status}`;
            const code = body && typeof body === 'object' && typeof body.code === 'string'
                ? body.code
                : undefined;
            throw new PlayProjectError(message, response.status, code, body);
        }
        const json = (await response.json());
        if (this._validateResponses && schema) {
            const result = schema.safeParse(json);
            if (!result.success) {
                // Surface drift loudly in dev so the SDK ↔ indexer contract gap
                // is fixed at the boundary instead of corrupting downstream UI.
                console.error(`[PlayProjectSDK] response schema validation failed for ${path}:`, result.error.issues);
            }
        }
        return json;
    }
    /**
     * Whether a thrown error justifies a retry. Retry on timeouts, 429, 5xx,
     * and network-level failures (fetch rejects with TypeError). Skip retry
     * on non-retryable PlayProjectError (4xx client errors) and unknown types.
     */
    isRetryableError(err) {
        if (err instanceof PlayProjectError) {
            return err.code === 'TIMEOUT' || err.isRetryable;
        }
        // `fetch` surfaces connection failures as TypeError in browsers + Node 18+.
        return err instanceof TypeError;
    }
    computeBackoffMs(attempt, err) {
        if (err instanceof PlayProjectError) {
            const hintSec = err.retryAfterSeconds;
            if (hintSec !== undefined) {
                return Math.min(hintSec * 1000, MAX_RETRY_AFTER_MS);
            }
        }
        const base = this._initialDelayMs * 2 ** attempt;
        // Full jitter up to 25% of base to avoid retry stampedes across clients.
        const jitter = Math.random() * base * 0.25;
        return base + jitter;
    }
    async request(path, params, schema) {
        let attempt = 0;
        let lastError;
        while (attempt <= this._maxRetries) {
            try {
                return await this.requestOnce(path, params, schema);
            }
            catch (err) {
                lastError = err;
                if (!this.isRetryableError(err) || attempt === this._maxRetries) {
                    throw err;
                }
                const delay = this.computeBackoffMs(attempt, err);
                await new Promise((resolve) => setTimeout(resolve, delay));
                attempt++;
            }
        }
        // Unreachable in practice — the loop either returns or throws — but keeps
        // the type checker happy since it can't prove the loop terminates.
        throw lastError;
    }
    // ===========================================================================
    // Collections API
    // ===========================================================================
    /**
     * Get all collections with optional filters and pagination.
     */
    async getCollections(filters, sort, pagination) {
        return this.request('/api/collections', { ...filters, sort, ...pagination }, paginatedSchema(CollectionSchema));
    }
    /**
     * Get a single collection by address.
     */
    async getCollection(address) {
        this.assertValidAddress(address, 'collection address');
        return this.request(`/api/collections/${address}`, undefined, CollectionSchema);
    }
    /**
     * Get collection statistics.
     */
    async getCollectionStats(address) {
        this.assertValidAddress(address, 'collection address');
        return this.request(`/api/collections/${address}/stats`, undefined, CollectionStatsSchema);
    }
    /**
     * Get NFTs in a collection.
     */
    async getCollectionNfts(address, filters, pagination) {
        this.assertValidAddress(address, 'collection address');
        return this.request(`/api/collections/${address}/nfts`, { ...filters, ...pagination }, paginatedSchema(NftSchema));
    }
    /**
     * Get events for a collection.
     */
    async getCollectionEvents(address, type, pagination) {
        this.assertValidAddress(address, 'collection address');
        return this.request(`/api/collections/${address}/events`, { type, ...pagination }, paginatedSchema(IndexedEventSchema));
    }
    // ===========================================================================
    // NFTs API
    // ===========================================================================
    /**
     * Get a single NFT by address.
     */
    async getNft(address) {
        this.assertValidAddress(address, 'nft address');
        return this.request(`/api/nfts/${address}`, undefined, NftWithCollectionSchema);
    }
    /**
     * Get event history for an NFT.
     */
    async getNftHistory(address, pagination) {
        this.assertValidAddress(address, 'nft address');
        return this.request(`/api/nfts/${address}/history`, pagination, paginatedSchema(IndexedEventSchema));
    }
    // ===========================================================================
    // Users API
    // ===========================================================================
    /**
     * Get NFTs owned by a user.
     */
    async getUserNfts(address, filters, pagination) {
        this.assertValidAddress(address, 'user address');
        return this.request(`/api/users/${address}/nfts`, { ...filters, ...pagination }, paginatedSchema(NftWithCollectionSchema));
    }
    /**
     * Get user's activity (events).
     */
    async getUserActivity(address, pagination) {
        this.assertValidAddress(address, 'user address');
        return this.request(`/api/users/${address}/activity`, pagination, paginatedSchema(IndexedEventSchema));
    }
    /**
     * Get collections created by a user.
     */
    async getUserCollections(address, pagination) {
        this.assertValidAddress(address, 'user address');
        return this.request(`/api/users/${address}/collections`, pagination, paginatedSchema(CollectionSchema));
    }
    /**
     * Get user statistics.
     */
    async getUserStats(address) {
        this.assertValidAddress(address, 'user address');
        return this.request(`/api/users/${address}/stats`, undefined, UserStatsSchema);
    }
    /**
     * Get count of NFTs owned by user for multiple collections (batch).
     * Returns a map of collection address -> count.
     */
    async getUserCollectionNftCounts(userAddress, collectionAddresses) {
        if (collectionAddresses.length === 0) {
            return {};
        }
        this.assertValidAddress(userAddress, 'user address');
        for (const addr of collectionAddresses) {
            this.assertValidAddress(addr, 'collection address');
        }
        return this.request(`/api/users/${userAddress}/nft-counts`, { collections: collectionAddresses.join(',') }, UserCollectionNftCountsSchema);
    }
    // ===========================================================================
    // NftCollection Transactions (for TonConnect)
    // ===========================================================================
    /**
     * Build a PublicMint transaction payload for TonConnect.
     *
     * Sends a `PublicMint { quantity }` message to the NftCollection contract.
     * The contract validates: mint is enabled, quantity <= max_per_wallet,
     * supply + quantity <= max_supply, and msg.value >= quantity * mint_price.
     *
     * Value calculation: (pricePerNft * quantity) + 0.15 TON gas overhead.
     * The 0.15 TON covers collection processing + individual NFT item deployment.
     *
     * @param params.collectionAddress - NftCollection contract address (any TON address format)
     * @param params.quantity - Number of NFTs to mint (must be >= 1)
     * @param params.pricePerNft - Price per NFT in nanotons (from collection.mintPrice)
     * @returns Transaction message for TonConnect `sendTransaction`
     * @throws Error if quantity < 1
     */
    createMintTransaction(params) {
        const { collectionAddress, quantity, pricePerNft } = params;
        if (quantity < 1) {
            throw new Error('Quantity must be at least 1');
        }
        const body = beginCell()
            .store(storePublicMint({
            $$type: 'PublicMint',
            quantity: BigInt(quantity),
        }))
            .endCell();
        const mintGas = MINT_GAS_PER_NFT * BigInt(quantity) + MINT_GAS_BASE;
        const totalValue = pricePerNft * BigInt(quantity) + mintGas;
        // Convert address to user-friendly format for TonConnect
        const address = Address.parse(collectionAddress).toString();
        return {
            address,
            amount: totalValue.toString(),
            payload: body.toBoc().toString('base64'),
        };
    }
    /**
     * Build a RequestReveal transaction payload for TonConnect.
     *
     * Sends a `RequestReveal {}` message to the NftItem contract.
     * The item forwards the request to its parent collection, which updates
     * the item's content URI from unrevealed to revealed metadata.
     * Only the NFT owner can reveal their own NFT.
     *
     * Value: 0.1 TON gas (covers NFT -> Collection -> NFT round-trip messages).
     *
     * @param params.nftAddress - NftItem contract address to reveal
     * @returns Transaction message for TonConnect `sendTransaction`
     */
    createUserRevealTransaction(params) {
        const { nftAddress } = params;
        const body = beginCell()
            .store(storeRequestReveal({
            $$type: 'RequestReveal',
        }))
            .endCell();
        // Convert address to user-friendly format for TonConnect
        const address = Address.parse(nftAddress).toString();
        return {
            address,
            amount: USER_REVEAL_GAS.toString(),
            payload: body.toBoc().toString('base64'),
        };
    }
    /**
     * Build a Withdraw transaction payload for TonConnect.
     *
     * Sends a `Withdraw { amount, to }` message to the NftCollection contract.
     * The contract checks: sender is owner, remaining balance >= minimum reserve
     * (prevents draining below operational gas requirements).
     *
     * Value: 0.02 TON gas.
     *
     * @param params.collectionAddress - NftCollection contract address
     * @param params.amount - Amount to withdraw in nanotons (must be > 0)
     * @param params.toAddress - Destination address for withdrawn funds
     * @returns Transaction message for TonConnect `sendTransaction`
     * @throws Error if amount <= 0
     */
    createWithdrawTransaction(params) {
        const { collectionAddress, amount, toAddress } = params;
        if (amount <= 0n) {
            throw new Error('Withdraw amount must be positive');
        }
        const body = beginCell()
            .store(storeWithdraw({
            $$type: 'Withdraw',
            amount,
            to: Address.parse(toAddress),
        }))
            .endCell();
        // Convert address to user-friendly format for TonConnect
        const address = Address.parse(collectionAddress).toString();
        return {
            address,
            amount: WITHDRAW_GAS.toString(),
            payload: body.toBoc().toString('base64'),
        };
    }
    // ===========================================================================
    // CollectionRegistry Transactions (for TonConnect)
    // ===========================================================================
    /**
     * Build a RegisterCollection transaction payload for TonConnect.
     *
     * Sends a `RegisterCollection { collection, name }` message to the
     * CollectionRegistry contract. Requires payment of the registration fee.
     *
     * Value: registrationFee + 0.05 TON gas.
     *
     * @param params.registryAddress - CollectionRegistry contract address
     * @param params.collectionAddress - NftCollection to register
     * @param params.collectionName - Display name for the registry
     * @param params.registrationFee - Fee in nanotons (query from registry contract)
     * @returns Transaction message for TonConnect `sendTransaction`
     */
    createRegisterCollectionTransaction(params) {
        const { registryAddress, collectionAddress, collectionName, registrationFee } = params;
        const body = beginCell()
            .store(storeRegisterCollection({
            $$type: 'RegisterCollection',
            collection: Address.parse(collectionAddress),
            name: collectionName,
        }))
            .endCell();
        const totalValue = registrationFee + REGISTRY_GAS;
        // Convert address to user-friendly format for TonConnect
        const address = Address.parse(registryAddress).toString();
        return {
            address,
            amount: totalValue.toString(),
            payload: body.toBoc().toString('base64'),
        };
    }
    /**
     * Build a VerifyCollection transaction payload for TonConnect.
     *
     * Sends a `VerifyCollection { collection }` message to the registry.
     * Only the registry owner can verify collections (admin operation).
     *
     * Value: 0.05 TON gas.
     *
     * @param params.registryAddress - CollectionRegistry contract address
     * @param params.collectionAddress - NftCollection to verify
     * @returns Transaction message for TonConnect `sendTransaction`
     */
    createVerifyCollectionTransaction(params) {
        const { registryAddress, collectionAddress } = params;
        const body = beginCell()
            .store(storeVerifyCollection({
            $$type: 'VerifyCollection',
            collection: Address.parse(collectionAddress),
        }))
            .endCell();
        // Convert address to user-friendly format for TonConnect
        const address = Address.parse(registryAddress).toString();
        return {
            address,
            amount: REGISTRY_GAS.toString(),
            payload: body.toBoc().toString('base64'),
        };
    }
    /**
     * Build an UnverifyCollection transaction payload for TonConnect.
     *
     * Sends an `UnverifyCollection { collection }` message to the registry.
     * Only the registry owner can unverify collections (admin operation).
     *
     * Value: 0.05 TON gas.
     *
     * @param params.registryAddress - CollectionRegistry contract address
     * @param params.collectionAddress - NftCollection to unverify
     * @returns Transaction message for TonConnect `sendTransaction`
     */
    createUnverifyCollectionTransaction(params) {
        const { registryAddress, collectionAddress } = params;
        const body = beginCell()
            .store(storeUnverifyCollection({
            $$type: 'UnverifyCollection',
            collection: Address.parse(collectionAddress),
        }))
            .endCell();
        // Convert address to user-friendly format for TonConnect
        const address = Address.parse(registryAddress).toString();
        return {
            address,
            amount: REGISTRY_GAS.toString(),
            payload: body.toBoc().toString('base64'),
        };
    }
    /**
     * Build a WithdrawFees transaction payload for TonConnect.
     *
     * Sends a `WithdrawFees { amount, to }` message to the registry.
     * Only the registry owner can withdraw accumulated registration fees.
     *
     * Value: 0.02 TON gas.
     *
     * @param params.registryAddress - CollectionRegistry contract address
     * @param params.amount - Amount to withdraw in nanotons (must be > 0)
     * @param params.toAddress - Destination address for withdrawn fees
     * @returns Transaction message for TonConnect `sendTransaction`
     * @throws Error if amount <= 0
     */
    createWithdrawFeesTransaction(params) {
        const { registryAddress, amount, toAddress } = params;
        if (amount <= 0n) {
            throw new Error('Withdraw amount must be positive');
        }
        const body = beginCell()
            .store(storeWithdrawFees({
            $$type: 'WithdrawFees',
            amount,
            to: Address.parse(toAddress),
        }))
            .endCell();
        // Convert address to user-friendly format for TonConnect
        const address = Address.parse(registryAddress).toString();
        return {
            address,
            amount: WITHDRAW_GAS.toString(),
            payload: body.toBoc().toString('base64'),
        };
    }
    // ===========================================================================
    // Utility Methods
    // ===========================================================================
    /**
     * Calculate the total cost for minting NFTs.
     * Includes per-NFT gas margin for multi-mint batches.
     */
    calculateMintCost(quantity, pricePerNft) {
        return pricePerNft * BigInt(quantity) + MINT_GAS_PER_NFT * BigInt(quantity) + MINT_GAS_BASE;
    }
    /** Gas fee for a single mint (qty=1). For multi-mint, use mintGasForQuantity(). */
    static get mintGas() {
        return MINT_GAS_PER_NFT + MINT_GAS_BASE;
    }
    /** Gas fee for minting a given quantity of NFTs. */
    static mintGasForQuantity(quantity) {
        return MINT_GAS_PER_NFT * BigInt(quantity) + MINT_GAS_BASE;
    }
    /** Gas fee for user reveal operations (NFT -> Collection -> NFT) */
    static get userRevealGas() {
        return USER_REVEAL_GAS;
    }
    /** Gas fee for withdraw operations */
    static get withdrawGas() {
        return WITHDRAW_GAS;
    }
    /** Gas fee for registry operations */
    static get registryGas() {
        return REGISTRY_GAS;
    }
}
